import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useDndMonitor, useDroppable } from "@dnd-kit/core";
import { cloneDeep, merge } from "lodash";
import { ReactNode, useEffect, useState } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useSetLayer from "@/core/helpers/useSetLayer";
import { useKeyPress } from "@/core/keyPressManager";
import { layer } from "@/domains/inventory/constants/layer";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  changeInventoryItem,
  inventoryMapSelector,
  memoizedItemSelector,
  setMemoizedItem,
} from "@/domains/inventory/slice";
import useInventoryManager from "@/domains/inventory/hooks/inventory/useInventoryManager";
import KeyCode from "@/assets/constants/key-code";

type Props = {
  children: ReactNode;
};

export default function InventoryCharacterDropSpaceContainer({ children }: Props) {
  const inventoryMap = useAppSelector(inventoryMapSelector);
  const dispatch = useAppDispatch();
  const memoizedItem = useAppSelector(memoizedItemSelector);
  const { currLayer } = useSetLayer(layer);
  const { subscribeKeyDown, unsubscribeKeyDown } = useKeyPress();
  const { dropItem } = useInventoryManager();
  const [isDragging, setIsDragging] = useState(false);
  const { setNodeRef } = useDroppable({
    id: "space",
  });

  useEffect(() => {
    const key = "inventory-handler";
    const subscribe = async (keyCode: KeyCode) => {
      // if (currLayer !== layer) return;
      switch (keyCode) {
        case KeyCode.R: {
          if (memoizedItem && memoizedItem.itemTemplate.isRotatable && isDragging) {
            const clonedItem = cloneDeep(memoizedItem);
            const diff: Partial<InventoryItem> = {
              isRotated: !clonedItem.isRotated,
              size: {
                width: clonedItem.size.height,
                height: clonedItem.size.width,
              },
            };
            const mergedItem = merge(clonedItem, diff);

            dispatch(setMemoizedItem(mergedItem));
            dispatch(
              changeInventoryItem({
                inventoryId: memoizedItem.inventoryId,
                inventoryItem: mergedItem,
              }),
            );
          }

          break;
        }
        default:
          break;
      }
    };

    subscribeKeyDown(key, subscribe);

    return () => {
      unsubscribeKeyDown(key);
    };
  }, [memoizedItem, unsubscribeKeyDown, isDragging]);

  useDndMonitor({
    onDragStart: () => {
      setIsDragging(true);
    },
    onDragEnd: async (event) => {
      setIsDragging(false);
      if (!event.over) return;

      const activeItem = event.active.data.current;

      if (!activeItem) return;
      if (inventoryMap[activeItem.inventoryId].ownerType === OwnerType.Environment) return;

      const overId = String(event.over.id);

      try {
        if (overId === "space") {
          await dropItem(activeItem.inventoryId, activeItem as InventoryItem);
        }
      } catch (e) {
        console.log(e);
      }
    },
  });
  return (
    <div
      ref={setNodeRef}
      className="fixed inset-0 px-[--boundary-offset-x] py-[--boundary-offset-y] flex items-end justify-end gap-5 z-[9999]"
    >
      {children}
    </div>
  );
}
